import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import {
  Badge,
  Button,
  Spinner,
  Tabs,
  TabsList,
  TabsTrigger,
  TooltipProvider,
} from '@monksflow/monks-ui';
import { useQuery } from '@tanstack/react-query';
import {
  GitBranch,
  Maximize2,
  Minimize2,
  RefreshCw,
  ShieldCheck,
  TriangleAlert,
} from 'lucide-react';
import { useCurrentUser, usePermissions } from '@/entities/user';
import {
  previousVersion,
  taxonomyDetailQueryOptions,
  useAddTaxonomyVersion,
  clampFieldValue,
  clampFieldValues,
  useSetTaxonomyVersionStatus,
  useUpdateTaxonomyDraft,
  type TaxonomyField,
} from '@/entities/taxonomy';
import { substituteResolvedValues } from '@inference.engine/taxonomy/web';
import { track } from '@/shared/analytics';
import { PageTransition } from '@/shared/ui/animations';
import { ListPageLayout } from '@/shared/ui/layout';
import { ROUTES } from '@/shared/config/routes';
import { TaxonomyReasonModal } from '../TaxonomyList/components/TaxonomyReasonModal/TaxonomyReasonModal';
import { FieldsEditor, type FocusTarget } from './components/FieldsEditor/FieldsEditor';
import { HistoryDrawer } from './components/HistoryDrawer/HistoryDrawer';
import { TaxonomyDetailHeader } from './components/TaxonomyDetailHeader/TaxonomyDetailHeader';
import { VersionDiffModal } from './components/VersionDiffModal/VersionDiffModal';
import { RulesEditor } from './components/RulesEditor/RulesEditor';
import { SchemaPreview } from './components/SchemaPreview/SchemaPreview';
import { PublishBlockedLists } from './components/PublishBlockedLists/PublishBlockedLists';
import { useResolvedListValues } from '@/features/taxonomy-list-binding';
import {
  indexProblems,
  validateSchema,
  type SchemaProblem,
  type ValidateSchemaResult,
} from './lib/validateSchema';
import { resolveHead, type DraftState } from './lib/resolveHead';
import { useAutosaveDraft } from './lib/useAutosaveDraft';
import { useIdleCheck } from './lib/useIdleCheck';
import { isStructuralChange } from './lib/structuralChange';
import { SchemaProperties } from './components/SchemaProperties/SchemaProperties';
import { SchemaSettings } from './components/SchemaSettings/SchemaSettings';
import { ValidateSummary } from './components/ValidateSummary/ValidateSummary';
import { ValidationStrip } from './components/ValidationStrip/ValidationStrip';
import { withDisplayValuesAsCodes } from './lib/withDisplayValuesAsCodes';

/** Stable empty array so the resolver's memo doesn't churn before data lands. */
const NO_FIELDS: TaxonomyField[] = [];

export function TaxonomyDetail() {
  const { t } = useTranslation('knowledge');
  const { taxonomyId = '' } = useParams();
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { canManageTaxonomy } = usePermissions();

  const query = useQuery(taxonomyDetailQueryOptions(taxonomyId));
  const taxonomy = query.data;
  const head = useMemo(
    () => (taxonomy ? resolveHead(taxonomy.versions, taxonomy.currentVersionId) : undefined),
    [taxonomy],
  );
  const isDraft = head?.status === 'draft';

  const updateDraft = useUpdateTaxonomyDraft(taxonomyId, head?.id ?? '');
  const setStatus = useSetTaxonomyVersionStatus(taxonomyId);
  const addVersion = useAddTaxonomyVersion(taxonomyId);

  const [publishOpen, setPublishOpen] = useState(false);
  /** True while the pre-publish autosave flush is in flight. */
  const [publishing, setPublishing] = useState(false);
  /** Why the publish dialog refused, shown inside it rather than behind it. */
  const [publishBlocked, setPublishBlocked] = useState<string | null>(null);
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [newVersionOpen, setNewVersionOpen] = useState(false);
  const [restoreVersionId, setRestoreVersionId] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [tab, setTab] = useState<'fields' | 'rules'>('fields');
  // Which version is being VIEWED (null = the head). Lets users browse past
  // versions read-only via the version menu, like the projects asset pattern.
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);
  const [diffState, setDiffState] = useState<{
    fromId: string | null;
    toId: string;
    picker: boolean;
  } | null>(null);

  const {
    draft,
    edit: editDraft,
    unsaved,
    conflict,
    saveError,
    flushSave,
    retrySave,
    discardAndReseed,
  } = useAutosaveDraft({
    taxonomy,
    head,
    isDraft,
    saveDraftAsync: updateDraft.mutateAsync,
    saveFailedMessage: t('taxonomy.taxonomyDetail.yourChangesCouldNotSaved'),
  });

  /**
   * Abandons the local edits and reloads whatever the server now holds.
   *
   * A real merge would need per-field edits rather than whole-document saves, so
   * the honest options today are "keep theirs" or "copy mine out by hand" — this
   * is the first. `discardAndReseed` clears the seed marker so the hook re-seeds
   * against the refetched version instead of keeping the stale draft on screen.
   */
  const reloadAfterConflict = async () => {
    discardAndReseed();
    // The document these findings describe is the one being thrown away.
    autoCheck.cancel();
    setReport(null);
    await query.refetch();
  };

  /**
   * The report stays on screen until the next check replaces it.
   *
   * Most edits wait for the user to pause. A structural edit, and the edits
   * callers flag as `immediate` (paste, fill, clear, Add row, the one-click fix,
   * the shortener's applies, and the grid's dialog and menu actions), are
   * checked straight away unless something holds the check: the old findings
   * may describe columns that just changed, and nobody is in the middle of typing.
   */
  const edit = (patch: Partial<DraftState>, opts?: { immediate?: boolean }) => {
    const structural = !!patch.fields && isStructuralChange(view.fields, patch.fields);
    editDraft(patch);
    if (opts?.immediate || structural) autoCheck.runSoon();
    else autoCheck.schedule();
  };

  const handleBackToList = () => void navigate(ROUTES.KNOWLEDGE_HUB_TAXONOMY);

  const applyProblemFix = (problem: SchemaProblem) => {
    const fix = problem.fix;
    if (fix?.kind !== 'use_display_as_code') return;
    edit({ fields: withDisplayValuesAsCodes(view.fields, fix.fieldId) }, { immediate: true });
  };

  /**
   * Publishes the current draft, AFTER flushing whatever autosave still owes.
   *
   * `useDebounce` is trailing with no flush, so an edit made in the last 800ms
   * had not been sent yet — and publishing takes the version out of draft, which
   * turns that pending save from late into impossible: the effect's `isDraft`
   * guard dropped it silently, the published version went out without the edit,
   * and the builder re-rendered from server state so the edit visibly
   * disappeared. (When the ordering inverted, the PATCH lost to the publish and
   * came back 409 "Only draft versions can be edited", which the banner then
   * blamed on another user.) Flushing first makes "Publish" mean "publish what I
   * am looking at".
   */
  const handlePublish = async (reason: string) => {
    if (!head) return;
    setPublishing(true);
    let drained = false;
    try {
      drained = await flushSave();
    } finally {
      setPublishing(false);
    }
    // Anything still unsent means the version on the server is not the one on
    // screen, whether that is a conflict or a 500. Publishing would freeze the
    // wrong snapshot AND make the pending edit unsavable, so stop here; the
    // banner (409) or the save toast (anything else) has already said why.
    if (!drained) {
      // The dialog stays open with its button live again, so without this it
      // simply does nothing when pressed. A 409 has the banner behind the
      // modal; a 400 or a 500 has nothing the user can see from in here.
      setPublishBlocked(t('taxonomy.taxonomyDetail.yourChangesCouldNotSaved'));
      return;
    }
    setPublishBlocked(null);
    setStatus.mutate(
      { versionId: head.id, status: 'published', reason },
      { onSuccess: () => setPublishOpen(false) },
    );
  };

  /**
   * Takes the current published version back to draft.
   *
   * The undo for a publish you regret. Editing in place is deliberate rather
   * than branching a new version: a mistake caught right after publishing
   * shouldn't leave a bad v4 in the history with the fix as v5 — the version
   * numbers are supposed to mean something.
   */
  const handleUnpublish = (reason: string) => {
    // No flush needed, unlike publish: the version being unpublished is already
    // published, so it was never editable here and there is nothing pending.
    if (!head) return;
    setStatus.mutate(
      { versionId: head.id, status: 'draft', reason },
      { onSuccess: () => setUnpublishOpen(false) },
    );
  };

  // Branch a fresh editable draft off the current (published/archived) head. The
  // backend copies the head's fields/rules/markets forward, so editing continues
  // from the published state. On success the head moves to the new draft and the
  // seed effect re-seeds the editable draft.
  const handleCreateVersion = (reason: string) => {
    if (!taxonomy) return;
    addVersion.mutate(
      { expectedVersion: taxonomy.version, reason },
      {
        onSuccess: () => {
          setNewVersionOpen(false);
          setTab('fields');
          // Jump to the new head draft (clear any historical selection).
          setSelectedVersionId(null);
        },
      },
    );
  };

  /**
   * Roll back to an older version by branching FORWARD from it: a new draft
   * carrying that version's content becomes the head. Nothing is rewound or
   * deleted — superseded versions must survive, because anything pinned to
   * their snapshots still resolves against them.
   */
  const handleRestoreVersion = (reason: string) => {
    if (!taxonomy || !restoreVersionId) return;
    addVersion.mutate(
      { expectedVersion: taxonomy.version, parentVersionId: restoreVersionId, reason },
      {
        onSuccess: () => {
          setRestoreVersionId(null);
          setTab('fields');
          setSelectedVersionId(null);
        },
      },
    );
  };

  /**
   * The schema on screen, from whichever source owns it: the live editable
   * draft when the head draft is being viewed, else the selected version's
   * frozen snapshot. Derived here — ABOVE the early returns — because the hooks
   * below must run on every render, including while the query is still loading.
   *
   * `outputs` come from the version either way: the builder doesn't edit them,
   * so the draft state has no copy to go stale.
   */
  const visibleSchema = useMemo(() => {
    if (!taxonomy || !head) {
      return {
        versionId: null,
        fields: NO_FIELDS,
        rules: [],
        settings: undefined,
        outputs: [],
        platforms: [],
      };
    }
    const sel = taxonomy.versions.find((v) => v.id === selectedVersionId) ?? head;
    const editingHeadDraft = sel.id === head.id && head.status === 'draft';
    const source = editingHeadDraft && draft ? draft : sel;
    return {
      versionId: sel.id,
      fields: source.fields,
      rules: source.rules,
      settings: source.settings,
      outputs: sel.outputs,
      platforms: source.platform,
    };
  }, [taxonomy, head, selectedVersionId, draft]);

  const visibleFields = visibleSchema.fields;

  // Vocabularies pulled from shared lists. Kept SEPARATE from the fields so the
  // persisted schema never absorbs a list's options — the builder renders from
  // this map while autosave keeps writing the authoritative field state.
  const resolvedLists = useResolvedListValues(visibleFields);

  // A bound column resolves to an empty vocabulary until its list arrives, which
  // is indistinguishable from a deleted list to anything measuring it.
  const listsPending = Object.values(resolvedLists).some((l) => l.pending);

  /**
   * An edit checks the schema too, so a collision typed and left cannot sit
   * unseen until someone happens to publish.
   *
   * Only when the head draft is still what is on screen: the compute reads the
   * VIEWED version, and a report on a read-only version is one the user never
   * asked about. A version switch already cancels a pending check; together
   * with that cancel, this keeps a check from painting an older version.
   *
   * Held while shared lists load, like the Validate button: `validateSchema`
   * does not judge a pending binding, so a run mid-load would read clean.
   */
  const autoCheck = useIdleCheck(
    () => {
      if (visibleSchema.versionId !== head?.id) return;
      setReport(computeRef.current());
    },
    { listsPending },
  );

  /**
   * Validate: after a pause, not continuously.
   *
   * The checks are cheap enough to run on every keystroke, and that is exactly
   * why they must not — a grid that turns red while you are still typing the
   * value teaches you to ignore red. `report` is null until the first check, and
   * clearing it puts the grid back to normal without undoing anything.
   */
  const [report, setReport] = useState<ValidateSchemaResult | null>(null);

  /**
   * A report describes ONE version, so it cannot outlive the one on screen.
   *
   * Branching copies field ids forward verbatim, which means a stale report
   * does not harmlessly miss — its keys still resolve, and it paints the new
   * version's grid with findings computed against the old one.
   */
  const viewedVersionId = visibleSchema.versionId;
  // Read at the version switch: an open publish dialog needs a report for the
  // version now on screen, or Publish stays disabled with no reason given. The
  // check runs after the next render because the draft reseeds to the new head in
  // its own effect; computed now, it would judge the previous head's content.
  const publishOpenRef = useRef(false);
  publishOpenRef.current = publishOpen;
  useEffect(() => {
    autoCheck.cancel();
    setReport(null);
    if (publishOpenRef.current) autoCheck.runSoon();
  }, [viewedVersionId, autoCheck]);

  /**
   * The schema ON SCREEN, unsaved edits included — the whole point of keeping
   * this deterministic and client-side.
   *
   * Split from `runValidate` because the report is also produced without a
   * click, and `taxonomy_schema_validated` should keep meaning "a user checked
   * their work" rather than counting every publish attempt and every idle check.
   */
  const computeReport = useCallback(
    () => validateSchema({ ...visibleSchema, resolvedLists }),
    [visibleSchema, resolvedLists],
  );

  const runValidate = () => {
    const next = computeReport();
    setReport(next);
    track('taxonomy_schema_validated', {
      schema_id: taxonomyId,
      problem_count: next.problems.length,
      error_count: next.errorCount,
      warning_count: next.warningCount,
    });
  };

  // Re-indexed per report rather than per render: the grid looks up by cell on
  // every one of potentially thousands of cells. One call for all three views —
  // they are the same partition and must not drift apart.
  const {
    byCell: cellProblems,
    byField: fieldProblems,
    schema: schemaProblems,
  } = useMemo(() => indexProblems(report?.problems ?? []), [report]);

  /** Findings anchored somewhere in the grid rather than to the schema as a whole. */
  const anchoredProblems = useMemo(
    () => (report?.problems ?? []).filter((problem) => problem.target.kind !== 'schema'),
    [report],
  );

  /**
   * The finding the details popover last asked the grid to reveal.
   *
   * `nonce` rather than object identity: clicking the same finding twice has to
   * scroll to it twice, and an unchanged prop would do nothing the second time.
   */
  const [focusTarget, setFocusTarget] = useState<FocusTarget | null>(null);
  const jumpNonce = useRef(0);

  const jumpToProblem = useCallback(
    (problem: SchemaProblem) => {
      if (problem.target.kind === 'schema') return;
      // A jump can only land on the fields grid, so switch to it first.
      setTab('fields');
      jumpNonce.current += 1;
      setFocusTarget({
        fieldId: problem.target.fieldId,
        row: problem.target.kind === 'cell' ? problem.target.row : undefined,
        column: problem.target.kind === 'cell' ? problem.target.column : undefined,
        nonce: jumpNonce.current,
      });
    },
    [setTab],
  );

  /**
   * `focusTarget` is a command, consumed on the commit it arrives in.
   *
   * Child effects flush before parent effects in one commit, so the grid reveals
   * the cell before this clears it — including on the mount commit of a jump
   * taken from the rules tab, where `setTab` and the target land together and the
   * grid's mount run IS the reveal. That is why this clears the target instead of
   * the grid ignoring a repeat nonce: suppressing the mount run would break the
   * cross-tab jump entirely.
   *
   * Uncleared, the target outlives the report it came from. `FieldsEditor`
   * unmounts with the tab, a mount runs its reveal effect whatever the
   * dependencies say, and returning to the tab replayed the old jump, stealing
   * focus into the grid on the way.
   */
  useEffect(() => {
    if (focusTarget) setFocusTarget(null);
  }, [focusTarget]);

  /** Focus has to go somewhere when "Clear" removes the button holding it. */
  const validateButtonRef = useRef<HTMLButtonElement>(null);
  /** The fix closes the popover holding focus. */
  const applyProblemFixFromDetails = (problem: SchemaProblem) => {
    applyProblemFix(problem);
    validateButtonRef.current?.focus();
  };
  const clearReport = () => {
    autoCheck.cancel();
    setReport(null);
    validateButtonRef.current?.focus();
  };

  /**
   * Produces a report without waiting to be asked.
   *
   * No automatic run happens while a bound list is still resolving: the publish
   * and version-switch checks wait for the lists, and the idle check is held.
   * `validateSchema` deliberately does not judge a pending binding — reporting a
   * list that has not arrived as deleted would tell the user to unlink a
   * governed vocabulary over a cold cache — so a run mid-load comes back CLEAN
   * on a schema nobody has actually checked. That is the same reason the Validate
   * button itself is disabled while lists are pending.
   */
  const computeRef = useRef(computeReport);
  computeRef.current = computeReport;

  /**
   * Opening the publish dialog checks the schema, so the gate cannot be dodged
   * by never clicking Validate.
   *
   * Deps are the two conditions, NOT `computeReport`: its closure changes on
   * every keystroke, so depending on it would re-validate continuously while the
   * dialog is open. Reading it through a ref keeps the latest schema without
   * making identity a trigger — the same reason the autosave flush does it.
   *
   * Re-fires when `listsPending` clears, so a dialog opened over a cold cache
   * still gets a real report the moment the lists land. The one-click fix inside
   * the dialog re-checks through `edit` itself. Publishing flushes autosave
   * first, so a report on the unsaved draft does not let unsaved work publish.
   */
  useEffect(() => {
    if (publishOpen && !listsPending) setReport(computeRef.current());
  }, [publishOpen, listsPending]);

  // Fields with list-bound vocabularies materialized, for the preview + the
  // character counter (both need real values to measure).
  //
  // Shared with `validateSchema`, which used a DIFFERENT condition: this one
  // substituted whenever an entry existed, so a list still loading replaced the
  // vocabulary with nothing and the counter under-reported while the Validate
  // bar did not.
  const fieldsForPreview = useMemo(
    () => substituteResolvedValues(visibleFields, resolvedLists),
    [visibleFields, resolvedLists],
  );

  const loadingView = (
    <div className="flex flex-1 items-center justify-center py-20">
      <Spinner />
    </div>
  );

  if (query.isLoading) return loadingView;

  /**
   * `draft` is deliberately NOT part of this condition.
   *
   * It is seeded by an effect, so on a warm cache the first committed frame of a
   * SUCCESSFUL load has `taxonomy` and `head` but no `draft` yet — and folding
   * that in rendered the error view, complete with a way out, over a load that
   * was about to work. `resolveHead` returning nothing lands here first, so a
   * genuinely broken response still reports as broken.
   */
  if (query.isError || !taxonomy || !head) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 py-20 text-center">
        <p className="text-sm text-muted-foreground">
          {t('taxonomy.taxonomyDetail.taxonomyCouldnTLoaded')}
        </p>
        <Button variant="outline" onClick={handleBackToList}>
          {t('taxonomy.taxonomyDetail.backTaxonomy')}
        </Button>
      </div>
    );
  }

  // The one frame between a resolved query and the seed effect running.
  if (!draft) return loadingView;

  // The version being viewed (defaults to the head). Only the head draft is
  // editable; any other selection (older, published, archived) is read-only.
  const selected = taxonomy.versions.find((v) => v.id === selectedVersionId) ?? head;
  const viewingHeadDraft = selected.id === head.id && isDraft;
  /**
   * Whether the editing controls do anything. Separate from
   * {@link viewingHeadDraft} rather than folded into it, because the two answer
   * different questions and the copy keyed on the version state has to keep
   * saying what it means: "this version is published" explains a frozen grid to
   * someone who could edit a draft, and would be a lie told to someone who
   * could not. Every write endpoint behind this screen requires the manage
   * permission, so without it the only honest surface is a read-only one.
   */
  const canEdit = viewingHeadDraft && canManageTaxonomy;
  // Only the CURRENT published version can go back to draft — an older one would
  // leave two candidates for what the schema means (the backend enforces this too).
  const viewingPublishedHead = selected.id === head.id && head.status === 'published';

  // Render source: the live editable draft when editing the head draft, else a
  // read-only snapshot of the selected version.
  const view = viewingHeadDraft
    ? draft
    : {
        name: taxonomy.name,
        description: selected.description ?? '',
        market: selected.market,
        platform: selected.platform,
        fields: selected.fields,
        rules: selected.rules,
        settings: selected.settings,
      };

  const isViewingOlder = selected.id !== head.id;

  // Apply a PACK of abbreviation changes in ONE edit (looping a per-item apply
  // would each read the same stale `view.fields`, so only the last would land).
  const applyAbbreviations = (changes: { fieldId: string; value: string; to: string }[]) => {
    const byField = new Map<string, Map<string, string>>();
    for (const c of changes) {
      if (!byField.has(c.fieldId)) byField.set(c.fieldId, new Map());
      byField.get(c.fieldId)!.set(c.value, c.to);
    }
    edit(
      {
        fields: view.fields.map((f) => {
          const codes = byField.get(f.id);
          if (!codes) return f;
          return {
            ...f,
            values: f.values.map((v) =>
              codes.has(v.value) ? { ...v, code: clampFieldValue('code', codes.get(v.value)!) } : v,
            ),
          };
        }),
      },
      { immediate: true },
    );
  };

  /**
   * Applies date-mask changes from the shortener. Separate from
   * `applyAbbreviations` because a mask lives on the field (`format`), not on a
   * value's code — the two edits touch different things and are reviewed
   * separately.
   */
  const applyDateFormats = (changes: { fieldId: string; to: string }[]) => {
    const byField = new Map(changes.map((c) => [c.fieldId, c.to]));
    edit(
      {
        fields: view.fields.map((f) => {
          const format = byField.get(f.id);
          return format ? { ...f, format } : f;
        }),
      },
      { immediate: true },
    );
  };

  /**
   * Makes a list-bound field local so its codes can be shortened here.
   *
   * The alternative — rewriting the codes in the shared List — would reach every
   * schema bound to it. Unlinking copies the list's values onto the field (with
   * the shortened codes already applied) and drops the binding, so the change
   * stops at this schema. The field no longer receives list updates, which is
   * the trade the dialog states before this runs.
   */
  const applyUnlinks = (
    changes: { fieldId: string; values: { value: string; code: string }[] }[],
  ) => {
    const byField = new Map(changes.map((c) => [c.fieldId, c.values]));
    edit(
      {
        fields: view.fields.map((f) => {
          const values = byField.get(f.id);
          if (!values) return f;
          return { ...f, listRef: null, values: clampFieldValues(values) };
        }),
      },
      { immediate: true },
    );
  };

  const openCompare = () => {
    const prev = previousVersion(taxonomy.versions, head.id);
    setDiffState({ fromId: prev?.id ?? null, toId: head.id, picker: true });
  };
  const openDiffForVersion = (versionId: string) => {
    const prev = previousVersion(taxonomy.versions, versionId);
    setHistoryOpen(false); // avoid stacking the dialog over the drawer
    setDiffState({ fromId: prev?.id ?? null, toId: versionId, picker: false });
  };

  const mediaCount = view.fields.filter((f) => f.group === 'media').length;
  const contentCount = view.fields.filter((f) => f.group === 'content').length;

  // Prefer the backend-resolved last modifier; fall back to the signed-in user
  // when they are the one who last touched it (covers envs where the Platform
  // user lookup isn't reachable, e.g. local), then to the creator's name.
  const lastModifiedBy =
    taxonomy.modifiedBy?.name ??
    (taxonomy.updatedBy && taxonomy.updatedBy === currentUser?.id ? currentUser.name : null) ??
    taxonomy.creator?.name ??
    null;

  return (
    <TooltipProvider>
      <ListPageLayout>
        <PageTransition variant="slideUp" delay={0}>
          <TaxonomyDetailHeader
            schemaName={view.name}
            status={selected.status}
            canEdit={canEdit}
            conflict={conflict}
            saveError={saveError}
            isSaving={updateDraft.isPending || unsaved}
            onRetrySave={retrySave}
            versionMenuProps={{
              versions: taxonomy.versions,
              selectedId: selected.id,
              headId: head.id,
              canCreate: !isDraft,
              canManage: canManageTaxonomy,
              onSelect: (id) => setSelectedVersionId(id === head.id ? null : id),
              onCreateVersion: () => setNewVersionOpen(true),
              onOpenHistory: () => setHistoryOpen(true),
              onCompare: openCompare,
              onRestore: canManageTaxonomy ? setRestoreVersionId : undefined,
            }}
            showUnpublish={viewingPublishedHead && canManageTaxonomy}
            onUnpublish={() => setUnpublishOpen(true)}
            showPublish={canEdit}
            onPublish={() => {
              setPublishBlocked(null);
              setPublishOpen(true);
            }}
          />
        </PageTransition>

        {/* A save that failed for anything other than a conflict. The header chip
            carried this in a `title` alone, so the only way to learn that work
            was not saved was to hover the two words that said so. */}
        {saveError && !conflict && (
          <div className="mx-1 flex flex-wrap items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3">
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span role="alert" className="text-sm font-medium text-destructive">
                {saveError}
              </span>
              <span className="text-xs text-muted-foreground">
                {t('taxonomy.taxonomyDetail.editsStillOnScreenNextChange')}
              </span>
            </div>
            <Button variant="outline" size="mini" onClick={retrySave}>
              <RefreshCw className="size-3.5" />
              {t('taxonomy.taxonomyDetail.retrySave')}
            </Button>
          </div>
        )}

        {/* Concurrent-edit notice. Deliberately a persistent banner rather than a
            toast: autosave has stopped, the edits on screen are unsaved, and
            that's a state the user has to act on — not a message that should
            scroll away after four seconds. */}
        {conflict && (
          <div className="mx-1 flex flex-wrap items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/10 p-3">
            <TriangleAlert className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-sm font-medium text-destructive">
                {t('taxonomy.taxonomyDetail.someoneElseEditedSchemaYour')}
              </span>
              <span className="text-xs text-muted-foreground">
                {conflict} {t('taxonomy.taxonomyDetail.savingPausedSoYourCopy')}
              </span>
            </div>
            <Button variant="outline" size="mini" onClick={() => void reloadAfterConflict()}>
              <RefreshCw className="size-3.5" />
              {t('taxonomy.taxonomyDetail.reloadLatest')}
            </Button>
          </div>
        )}

        {/* Single page scroller. It scrolls only far enough to move Schema
            Properties out of view; the Schema Content card is `sticky top-0`
            and `h-full`, so once its top reaches the top it pins and fills the
            viewport, and from there the rows scroll inside the card. */}
        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
          <PageTransition variant="slideUp" delay={0.05}>
            <SchemaProperties
              name={view.name}
              description={view.description}
              scope={taxonomy.scope}
              markets={view.market}
              platforms={view.platform}
              mediaFieldCount={mediaCount}
              contentFieldCount={contentCount}
              lastModifiedBy={lastModifiedBy}
              lastModifiedAt={taxonomy.updatedAt}
              readOnly={!canEdit}
              canManage={canManageTaxonomy}
              onNameChange={(name) => edit({ name })}
              onDescriptionChange={(description) => edit({ description })}
              onMarketsChange={(market) => edit({ market })}
              onPlatformsChange={(platform) => edit({ platform })}
            />
          </PageTransition>

          <PageTransition variant="slideUp" delay={0.075}>
            <SchemaPreview
              fields={fieldsForPreview}
              settings={view.settings}
              platforms={view.platform}
              taxonomyId={taxonomyId}
              versionId={selected.id}
              onApplyAbbreviations={canEdit ? applyAbbreviations : undefined}
              onApplyDateFormats={canEdit ? applyDateFormats : undefined}
              onApplyUnlinks={canEdit ? applyUnlinks : undefined}
            />
          </PageTransition>

          <div
            className={
              fullscreen
                ? 'fixed inset-0 z-50 flex flex-col gap-4 overflow-hidden bg-card p-6'
                : 'sticky top-0 flex h-full flex-col gap-4 rounded-xl border border-border-light bg-card p-5'
            }
          >
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-foreground">
                {t('taxonomy.taxonomyDetail.schemaContent')}
              </h2>
              <div className="flex items-center gap-1">
                <Button
                  ref={validateButtonRef}
                  variant="outline"
                  size="mini"
                  onClick={runValidate}
                  // Validating before the shared lists land reports every bound
                  // column as broken, so the button waits for them.
                  disabled={listsPending}
                  title={
                    listsPending ? t('taxonomy.taxonomyDetail.waitingSharedListsLoad') : undefined
                  }
                >
                  <ShieldCheck className="size-3.5" />
                  {t('taxonomy.taxonomyDetail.validate')}
                </Button>
                <Button
                  variant="ghost"
                  size="mini"
                  aria-label={
                    fullscreen
                      ? t('taxonomy.taxonomyDetail.exitFullScreen')
                      : t('taxonomy.taxonomyDetail.fullScreen')
                  }
                  onClick={() => setFullscreen((f) => !f)}
                >
                  {fullscreen ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
                </Button>
              </div>
            </div>

            <ValidationStrip
              report={report}
              anchoredProblems={anchoredProblems}
              schemaProblems={schemaProblems}
              tab={tab}
              onJumpToProblem={jumpToProblem}
              onFix={canEdit ? applyProblemFixFromDetails : undefined}
              onClear={clearReport}
              onDetailsOpenChange={(open) => autoCheck.setHeld('popover', open)}
              onFocusLost={() => validateButtonRef.current?.focus()}
            />

            <Tabs value={tab} onValueChange={(v) => setTab(v as 'fields' | 'rules')}>
              <TabsList>
                {/* eslint-disable i18next/no-literal-string -- tab keys, not copy */}
                <TabsTrigger value="fields">{t('taxonomy.taxonomyDetail.fields')}</TabsTrigger>
                <TabsTrigger value="rules">
                  {t('taxonomy.taxonomyDetail.rules')}
                  <Badge
                    variant="ghost"
                    roundness="full"
                    className="ml-1.5 border-border-light text-[10px]"
                  >
                    {view.rules.length}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            {isViewingOlder ? (
              <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border-light bg-surface/40 px-3 py-2 text-xs text-muted-foreground">
                <span>
                  {t('taxonomy.taxonomyDetail.youReViewingV')}
                  {selected.versionNumber} ({selected.status}
                  {t('taxonomy.taxonomyDetail.pastVersionReadOnly')}
                </span>
                <Button variant="outline" size="mini" onClick={() => setSelectedVersionId(null)}>
                  {t('taxonomy.taxonomyDetail.goCurrentV')}
                  {head.versionNumber})
                </Button>
              </div>
            ) : (
              // Only offered to someone who could act on it: the copy names
              // starting a new version as the way out of a frozen grid, which is
              // not the way out for a viewer who cannot write at all.
              !isDraft &&
              canManageTaxonomy && (
                <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border-light bg-surface/40 px-3 py-2 text-xs text-muted-foreground">
                  <span>
                    {t('taxonomy.taxonomyDetail.version')} {head.status}{' '}
                    {t('taxonomy.taxonomyDetail.readOnlyStartNewVersion')}
                  </span>
                  <Button variant="outline" size="mini" onClick={() => setNewVersionOpen(true)}>
                    <GitBranch className="size-3.5" />
                    {t('taxonomy.taxonomyDetail.newVersion')}
                  </Button>
                </div>
              )
            )}

            {tab === 'fields' ? (
              <FieldsEditor
                fields={view.fields}
                readOnly={!canEdit}
                onChange={(fields, opts) => edit({ fields }, opts)}
                onEditingChange={(open) => autoCheck.setHeld('editor', open)}
                taxonomyId={taxonomyId}
                versionId={selected.id}
                resolvedLists={resolvedLists}
                mintedCount={taxonomy.trackingCodeCount}
                cellProblems={cellProblems}
                fieldProblems={fieldProblems}
                focusTarget={focusTarget}
              />
            ) : (
              <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto pb-1">
                <SchemaSettings
                  settings={view.settings}
                  platforms={view.platform}
                  fields={view.fields}
                  taxonomyId={taxonomyId}
                  versionId={selected.id}
                  readOnly={!canEdit}
                  canMint={canManageTaxonomy}
                  trackingCodeCount={taxonomy.trackingCodeCount}
                  onChange={(settings) => edit({ settings })}
                />
                <RulesEditor
                  // Hydrated, not raw: a bound column's inline `values` are kept
                  // deliberately stale so unlinking can restore them, and are
                  // empty outright for a column bound at creation — so authoring
                  // a rule against one was impossible.
                  fields={fieldsForPreview}
                  rules={view.rules}
                  readOnly={!canEdit}
                  onChange={(rules) => edit({ rules })}
                />
              </div>
            )}
          </div>
        </div>
      </ListPageLayout>

      <HistoryDrawer
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        taxonomyId={taxonomyId}
        versions={taxonomy.versions}
        currentUserId={currentUser?.id}
        currentUserName={currentUser?.name}
        onSeeChanges={openDiffForVersion}
      />

      {diffState && (
        <VersionDiffModal
          open
          onClose={() => setDiffState(null)}
          versions={taxonomy.versions}
          initialFromId={diffState.fromId}
          initialToId={diffState.toId}
          allowPicker={diffState.picker}
        />
      )}

      {publishOpen && (
        <TaxonomyReasonModal
          open
          title={t('taxonomy.taxonomyDetail.publishNamed', { name: draft.name })}
          description={t('taxonomy.taxonomyDetail.publishingMakesVersionActiveNaming')}
          // What publishing will do to the vocabularies this schema reads. A list
          // with no published version blocks it outright; a list with newer work
          // does not, but the schema will not read that work and the person who
          // just made it should hear so before confirming, not after.
          extra={
            <>
              <PublishBlockedLists
                resolved={Object.values(resolvedLists)}
                schemaName={draft.name}
              />
              {/* Why confirm is dead. A disabled button with no reason beside it
                is a worse bug than the one this gate fixes, and focus goes to
                the reason textarea below, so a screen reader would otherwise
                never reach the explanation. */}
              {listsPending && (
                <p role="alert" className="text-muted-foreground">
                  {t('taxonomy.taxonomyDetail.waitingSharedListsLoad')}
                </p>
              )}
              {publishBlocked && (
                <p role="alert" className="text-destructive">
                  {publishBlocked}
                </p>
              )}
              {report && report.errorCount > 0 && (
                <div role="alert" className="flex items-start gap-2">
                  <ValidateSummary
                    report={report}
                    anchoredProblems={anchoredProblems}
                    schemaProblems={schemaProblems}
                    tab={tab}
                    onFix={canEdit ? applyProblemFix : undefined}
                  />
                </div>
              )}
            </>
          }
          confirmLabel={t('taxonomy.taxonomyDetail.publish')}
          // Covers the pre-publish autosave flush too, so the button can't be
          // pressed twice while the pending edit is still being written.
          isSubmitting={setStatus.isPending || publishing}
          /**
           * A Validate error now actually stops a publish, rather than being red
           * and ignorable.
           *
           * `listsPending` and `!report` both fail CLOSED. A report produced while
           * a bound list is still resolving skips that binding by design, so it
           * can read clean on a broken schema — and between this dialog opening
           * and its forced run landing there is no report at all, which would
           * otherwise read as zero errors.
           *
           * Errors only, not `problems.length`: warnings are advice, and blocking
           * on them would teach people that the gate is noise.
           *
           * `!report` has no test of its own, and cannot: the forced run lands
           * before any observation point a test can reach — even reading the DOM
           * synchronously after a `flushSync`ed click, which is how this suite
           * inspects pre-effect frames elsewhere. Every scenario that reaches
           * `!report` also has `listsPending` true, which IS covered. A test here
           * would pin React's scheduling rather than anything a user can do.
           *
           * No second look at `resolvedLists.error` here — a list that failed to
           * resolve is already a `broken_list_binding` error inside the report.
           * The `.unpublished` clause beside this is the cost of the other
           * approach: it is a hand-rolled copy of a judgment the engine already
           * makes, and it misses the error-only case as a result.
           */
          confirmDisabled={
            Object.values(resolvedLists).some((r) => r.unpublished) ||
            listsPending ||
            !report ||
            report.errorCount > 0
          }
          onCancel={() => {
            setPublishBlocked(null);
            setPublishOpen(false);
          }}
          onConfirm={(reason) => void handlePublish(reason)}
        />
      )}

      {unpublishOpen && (
        <TaxonomyReasonModal
          open
          title={t('taxonomy.taxonomyDetail.unpublishNamed', { name: view.name })}
          description={t('taxonomy.taxonomyDetail.unpublishDescription', {
            version: selected.versionNumber,
          })}
          confirmLabel={t('taxonomy.taxonomyDetail.unpublish')}
          isSubmitting={setStatus.isPending}
          onCancel={() => setUnpublishOpen(false)}
          onConfirm={handleUnpublish}
        />
      )}

      {restoreVersionId &&
        (() => {
          const target = taxonomy.versions.find((v) => v.id === restoreVersionId);
          if (!target) return null;
          return (
            <TaxonomyReasonModal
              open
              title={t('taxonomy.taxonomyDetail.restoreVersionTitle', {
                version: target.versionNumber,
              })}
              description={t('taxonomy.taxonomyDetail.restoreVersionDescription', {
                draft: head.versionNumber + 1,
                source: target.versionNumber,
                head: head.versionNumber,
              })}
              confirmLabel={t('taxonomy.taxonomyDetail.restoreVersion')}
              isSubmitting={addVersion.isPending}
              onCancel={() => setRestoreVersionId(null)}
              onConfirm={handleRestoreVersion}
            />
          );
        })()}

      {newVersionOpen && (
        <TaxonomyReasonModal
          open
          title={t('taxonomy.taxonomyDetail.newVersionOf', { name: draft.name })}
          description={t('taxonomy.taxonomyDetail.newVersionDescription', {
            draft: head.versionNumber + 1,
            head: head.versionNumber,
          })}
          confirmLabel={t('taxonomy.taxonomyDetail.createVersion')}
          isSubmitting={addVersion.isPending}
          onCancel={() => setNewVersionOpen(false)}
          onConfirm={handleCreateVersion}
        />
      )}
    </TooltipProvider>
  );
}

TaxonomyDetail.displayName = 'TaxonomyDetail';
